import { Badge, type BadgeTone } from "@/components/ui/Badge";
import { cn } from "@/lib/utils";

import type {
  ProductionTestDeviceStatus,
  ProductionTestEffectiveRow,
  ProductionTestEffectiveStatus,
} from "./calTestApi";

export function productionTestStatusTone(status: ProductionTestEffectiveStatus): BadgeTone {
  if (status === "pass") return "success";
  if (status === "fail") return "danger";
  return "warning";
}

export function productionTestStatusLabel(status: ProductionTestEffectiveStatus): string {
  if (status === "pass") return "Pass";
  if (status === "fail") return "Fail";
  return "Pending";
}

export function productionTestScopeMessage(status: ProductionTestDeviceStatus | null | undefined): string | null {
  if (status == null) {
    return "No active serial_fci episode for this MCU.";
  }
  if (status.scopeStatus === "no_valid_calibration") {
    return "No valid PCBA calibration for the current configuration scope.";
  }
  if (status.scopeStatus === "no_uploads") {
    return "No production test uploads for this configuration and calibration yet.";
  }
  return null;
}

export function ProductionTestMatrix({
  status,
  className,
  selectedTestKeys,
  onToggleTestKey,
  selectable = false,
}: {
  status: ProductionTestDeviceStatus | null | undefined;
  className?: string;
  selectedTestKeys?: Set<string>;
  onToggleTestKey?: (testKey: string) => void;
  selectable?: boolean;
}): JSX.Element | null {
  if (!status || status.tests.length === 0) {
    return null;
  }

  return (
    <div className={cn("space-y-2", className)}>
      {status.sequenceName ? (
        <p className="text-xs text-muted-foreground">
          Plan: {status.sequenceName}
          {status.sequenceSet ? ` (${status.sequenceSet})` : ""}
          {status.sequenceVersion ? ` · rev ${status.sequenceVersion}` : ""}
        </p>
      ) : null}
      <div className="grid gap-1">
        {status.tests.map((row) => (
          <ProductionTestMatrixRow
            key={row.testKey}
            row={row}
            selectable={selectable && row.status === "fail"}
            selected={selectedTestKeys?.has(row.testKey) ?? false}
            onToggle={() => onToggleTestKey?.(row.testKey)}
          />
        ))}
      </div>
    </div>
  );
}

function ProductionTestMatrixRow({
  row,
  selectable,
  selected,
  onToggle,
}: {
  row: ProductionTestEffectiveRow;
  selectable: boolean;
  selected: boolean;
  onToggle: () => void;
}): JSX.Element {
  return (
    <label
      className={cn(
        "flex items-center justify-between gap-2 rounded border px-2 py-1 text-xs",
        selectable ? "cursor-pointer" : "",
        selected ? "border-primary bg-primary/5" : "border-border",
      )}
    >
      <span className="flex min-w-0 items-center gap-2">
        {selectable ? (
          <input type="checkbox" checked={selected} onChange={onToggle} className="h-3.5 w-3.5 shrink-0" />
        ) : null}
        <span className="truncate">{row.testName}</span>
      </span>
      <Badge tone={productionTestStatusTone(row.status)}>{productionTestStatusLabel(row.status)}</Badge>
    </label>
  );
}
